import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Grid,
  Typography,
  TextField,
  Autocomplete,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Dialog,
  Tooltip, DialogContent,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import FancyPaper from "../../components/FancyPaper";
import { useApp } from "../../hooks/useApp";
import {
  getRequestCoworkers,
  getRequestProjectsByManager,
} from "../../services/projects/ProjectService"; // Asegúrate de importar createAdvance
import {
  getStaffListsByProject,
  getMembersByStaffList,
} from "../../services/projects/StaffListService";
import { useLocation } from "react-router-dom";
import WeekFilter from "../../components/utils/credit_view/WeekFilter2";
import AddOrUpdateCoworkerModal from "../../components/utils/AddOrUpdateCoworkerModal";
import AddIcon from "@mui/icons-material/Add";
import EventRepeat from "@mui/icons-material/EventRepeat";
import WarningIcon from "@mui/icons-material/Warning";
import {
  createAdvance,
  deleteAdvanceRequest,
  getRequestDetailsAdvances,
  rescheduleAdvance,
} from "../../services/advances/advancesService";
import { formatNumber, formatHours } from "../../components/utils/formatters";
import FancyTooltip from "../../components/utils/FancyTooltip";
import { TrashIcon, PenIcon } from "../../components/svgicons/SvgIcons";
import DeleteModal from "../../components/utils/DeleteModal";
import FancyDialogActions from "../../components/utils/FancyDialogActions";
import { useForm } from "react-hook-form";
import dayjs, { Dayjs } from "dayjs";
import ResourceAccess from "../../components/security/ResourceAccess";
import {
  Coworker, ProjectByManager,
  ProjectProgress,
  RescheduleAdvanceRequest,
  WeekFilterDateRangeProps
} from "../../types/project-progress/types";
import {getDateRange, getFirstDayOfCurrentWeek, getWeekNumber} from "../../utils/date-utils";

/** Orden de las columnas de la grilla: la semana de planilla arranca en jueves. */
const WEEK_DAYS = [
  "thursday",
  "friday",
  "saturday",
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
] as const;

const VACATION_COLOR = "#0288d1";

/**
 * Celda de horas de un día del avance.
 *
 * Los feriados van en verde, como siempre. Los días de vacaciones van en azul
 * con tooltip: ese día se paga como jornada completa en el proyecto
 * "Vacaciones", así que en un proyecto normal queda en cero y no admite horas.
 */
const WorkedHoursDayCell = ({
  hours,
  isHoliday,
  isVacation,
}: {
  hours: number;
  isHoliday: boolean;
  isVacation: boolean;
}) => {
  const cell = (
    <TableCell
      align="center"
      style={{
        color: isVacation ? VACATION_COLOR : isHoliday ? "green" : "inherit",
        fontWeight: isVacation || isHoliday ? "bold" : "inherit",
        backgroundColor: isVacation ? "rgba(2, 136, 209, 0.12)" : "inherit",
      }}
    >
      {formatHours(hours)}
    </TableCell>
  );

  if (!isVacation) {
    return cell;
  }

  return (
    <Tooltip
      placement="top"
      title={
        Number(hours) > 0
          ? "Vacaciones: el sistema acredita la jornada completa y el día no se puede editar"
          : "Vacaciones: no se pueden cargar horas este día. Se paga como jornada completa en el proyecto Vacaciones"
      }
    >
      {cell}
    </Tooltip>
  );
};

const DetailsAdvancesAdd = () => {
  const theme = useTheme();
  const {
    setLoading,
    setErrorMsg,
    modalData,
    setModalData,
    authInfo,
    setSuccessMsg,
  } = useApp();
  const location = useLocation();
  const { managerId: manager, id: id, startDate, endDate } = location.state || {};

  const [initialStartDate, setInitialStartDate] = useState<Dayjs | null>(
    startDate ? dayjs(startDate) : null
  );
  const [initialEndDate, setInitialEndDate] = useState<Dayjs | null>(
    endDate ? dayjs(endDate) : null
  );

  const createDefaultWeekFilterProps = useCallback((): WeekFilterDateRangeProps => {
    const firstDayOfWeek: Dayjs = getFirstDayOfCurrentWeek();
    return {
      startDate: firstDayOfWeek,
      endDate: firstDayOfWeek.add(6, "day").endOf("day"),
      disabled: false
    };
  }, []);

  const getCurrentWeekNumber = (): number => {
    const firstDayOfWeek: Dayjs = getFirstDayOfCurrentWeek();
    return getWeekNumber(firstDayOfWeek.add(6, "day").endOf("day"));
  };

  const { handleSubmit } = useForm();

  const [details, setDetails] = useState<ProjectProgress | null>(null);
  const [projects, setProjects] = useState<ProjectByManager[]>([]);
  const [coworkersData, setCoworkersData] = useState<Coworker[]>([]);
  const [selectedProject, setSelectedProject] = useState<any | null>(null);
  // Ids de personas que pertenecen a las listas de personal del proyecto seleccionado.
  // null = aún sin cargar; Set vacío = el proyecto no tiene listas o están sin miembros.
  const [projectStaffPersonIds, setProjectStaffPersonIds] = useState<Set<number> | null>(null);
  const [filteredWeek, setFilteredWeek] = useState<number>(() => getCurrentWeekNumber());
  const [dateRange, setDateRange] = useState<string>(() => {
    const firstDay = getFirstDayOfCurrentWeek();
    return getDateRange(firstDay, firstDay.add(6, "day").endOf("day"));
  });
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [advanceCreated, setAdvanceCreated] = useState(false);
  const [advanceData, setAdvanceData] = useState<any>(null);
  const [weekFilterProps, setWeekFilterProps] = useState<WeekFilterDateRangeProps>(() => createDefaultWeekFilterProps());
  const [showModal, setShowModal] = useState(false);
  const [dialogMessage, setDialogMessage] = useState(() => "");
  const [action, setAction] = useState<() => void>(() => {});

  const hideModal = () => {
    setShowModal(false);
    setAction(() => {});
  }

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    if (initialStartDate && initialEndDate) {
      localStorage.setItem("startDate", initialStartDate.format("YYYY-MM-DD"));
      localStorage.setItem("endDate", initialEndDate.format("YYYY-MM-DD"));
    }
  }, [initialStartDate, initialEndDate]);

  useEffect(() => {
    if (details?.header && projects.length > 0) {
      const projectFound = projects.find(
        (project) => project.code === details.header.code
      );
      if (projectFound) {
        setSelectedProject(projectFound);
      }
    }
  }, [details, projects]);

  const onSubmit = async (data: any) => {
    switch (modalData?.modalType) {
      case "delete":
        //@ts-ignore
        await handleDelete(modalData?.modalObject);
        break;
      default:
        break;
    }
  };

  const fetchProjects = async () => {
    try {
      const requestId = advanceData?.id ?? id;
      setLoading(true);
      if (requestId){
        const [newProjectProgress, coworkers, projectsByManager] = await Promise.all([
          getRequestDetailsAdvances(0,1000, requestId),
          getRequestCoworkers(requestId),
          getRequestProjectsByManager(manager)
        ]);
        setProjects(projectsByManager);
        setCoworkersData(coworkers);
        setDetails(newProjectProgress);
        loadWeekFilterProps(newProjectProgress);
      } else {
        const projectsByManager: any = await getRequestProjectsByManager(manager);
        setProjects(projectsByManager);
      }
    } catch (error: any) {
      resetData();
      setErrorMsg(error);
    } finally {
      setLoading(false);
    }
  }

  const resetData = () => {
    setCoworkersData([]);
    setDetails(null);
    setProjects([]);
    setWeekFilterProps(createDefaultWeekFilterProps());
  }

  const loadWeekFilterProps = (projectProgress: ProjectProgress) => {
    const startDate: Dayjs = dayjs(projectProgress.header.firstDayOfWeek, "DD-MM-YYYY");
    const endDate: Dayjs = dayjs(projectProgress.header.lastDayOfWeek, "DD-MM-YYYY");
    const {editable} = projectProgress.header;
    setDateRange(getDateRange(startDate, endDate));
    setYear(endDate.year());
    setFilteredWeek(getWeekNumber(endDate));
    setWeekFilterProps({startDate, endDate, disabled: !editable});
  }

  useEffect(() => {
    const dataInit = async () => {
      if (!advanceData?.id) return;

      try {
        setLoading(true);
        const data: Coworker[] = await getRequestCoworkers(advanceData?.id);
        setCoworkersData(data);
      } catch (error: any) {
        setErrorMsg(error);
      } finally {
        setLoading(false);
      }
    };
    dataInit();
  }, [advanceData]);

  // Carga las listas de personal del proyecto seleccionado y arma la unión (mix) de
  // los miembros de todas sus listas. Ese conjunto define qué colaboradores se pueden
  // seleccionar al agregar horas en el avance.
  useEffect(() => {
    const loadProjectStaff = async () => {
      const projectId = selectedProject?.id;
      if (!projectId) {
        setProjectStaffPersonIds(null);
        return;
      }

      try {
        const staffLists: any[] = (await getStaffListsByProject(projectId)) ?? [];
        if (staffLists.length === 0) {
          // Proyecto sin listas de personal => sin colaboradores disponibles.
          setProjectStaffPersonIds(new Set<number>());
          return;
        }

        const membersResults = await Promise.allSettled(
          staffLists.map((staffList: any) => getMembersByStaffList(staffList.id))
        );

        const personIds = new Set<number>();
        membersResults.forEach((result) => {
          if (result.status === "fulfilled") {
            (result.value ?? []).forEach((member: any) => {
              if (member?.personId != null) {
                personIds.add(member.personId);
              }
            });
          }
        });
        setProjectStaffPersonIds(personIds);
      } catch (error: any) {
        // Ante un fallo se deja la lista vacía para no ofrecer colaboradores ajenos al proyecto.
        setProjectStaffPersonIds(new Set<number>());
        setErrorMsg(error);
      }
    };

    loadProjectStaff();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject?.id]);

  // Colaboradores del avance restringidos a los que pertenecen a las listas de personal
  // del proyecto. Si el proyecto no tiene listas (o están vacías), queda vacío.
  const projectCoworkersData = useMemo<Coworker[]>(() => {
    if (!projectStaffPersonIds) return [];
    return coworkersData.filter((coworker: Coworker) =>
      projectStaffPersonIds.has(coworker.id)
    );
  }, [coworkersData, projectStaffPersonIds]);

  const handleChange = (selectedProject: any) => {
    setSelectedProject(selectedProject);
  };

  // Función para crear el avance
  const handleCreateAdvance = async () => {
    if (!selectedProject || !year || !filteredWeek) {
      setErrorMsg("Por favor, selecciona un proyecto, año y semana.");
      return;
    }

    try {
      setLoading(true);
      const data = {
        year,
        week: filteredWeek,
        projectId: selectedProject.id,
        user: authInfo?.username, // Cambiar de authInfo?.id a authInfo?.username
      };

      // Enviar el objeto `data` al endpoint
      const response = await createAdvance(data);
      setAdvanceData(response);
      setAdvanceCreated(true);
    } catch (error: any) {
      setErrorMsg(error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = async (event: any) => {
    event.preventDefault();
    const modalAction = event.currentTarget.getAttribute("data-name");
    let object = null;
    const id = event.currentTarget.getAttribute("data-id");
    const position_id = event.currentTarget.getAttribute("data-position-id");

    if (modalAction === "delete") {
      object = details?.details.find((p: any) => p.id === parseInt(id) && p.positionId === parseInt(position_id));
    } else if (modalAction === "edit") {
      object = details?.details.find((p: any) => p.id === parseInt(id) && p.positionId === parseInt(position_id));
    }

    //open modal
    setModalData &&
      setModalData({
        ...modalData,
        modalOpen: true,
        modalType: modalAction,
        modalObject: object,
      });
  };

  const handleOpenHoursModal = (data: any) => {
    setModalData &&
      setModalData({
        modalOpen: true,
        modalType: "addHours",
        modalObject: data,
      });
  };

  const handleCloseHoursModal = () => {
    setModalData &&
      setModalData({ modalOpen: false, modalType: "", modalObject: null });
  };

  const handleDelete = async (data: any) => {
    handleCloseHoursModal();
    setLoading && setLoading(true);
    try {
      let deleteData = await deleteAdvanceRequest({
        ...data,
        advanceId: advanceData?.id ? advanceData?.id : id,
      });
      if (!deleteData) {
        setErrorMsg &&
          setErrorMsg("Error al eliminar el registro, intente nuevamente");
        setLoading && setLoading(false);
        return;
      }
      setLoading && setLoading(false);
      setSuccessMsg && setSuccessMsg("Registro eliminado correctamente");
      //reset page and call fetch data
      await fetchProjects();
    } catch (error: any) {
      setErrorMsg && setErrorMsg(error.message);
      setLoading && setLoading(false);
    }
  };

  const showConfirmationDialog = (message: string, callBack: () => void) => {
    setDialogMessage(message);
    setShowModal(true);
    setAction(()=> callBack);
  }

  const showRescheduleProjectConfirmationMessage = () => {
    const message = "¿Confirma que desea mover de semana toda la planilla del avance de proyecto? Tome en cuenta que algunas fechas pueden trasladarse a feriados de pago obligatorio";
    showConfirmationDialog(message, rescheduleProject);
  }

  const rescheduleProject = async () => {
    setShowModal(false);
    setLoading(true);
    try{
      const request: RescheduleAdvanceRequest = {
        projectProgressId: advanceData?.id ?? id,
        user: authInfo.username,
        week: filteredWeek,
        year: year,
      };
      const newDetails: ProjectProgress = await rescheduleAdvance(request);
      if (newDetails) {
        loadWeekFilterProps(newDetails);
        setDetails(newDetails);
        setSuccessMsg("Avance de proyecto reprogramado correctamente");
      }
    } catch (error: any) {
      (error.message && setErrorMsg(error.message)) ||setErrorMsg(error);
    } finally {
      setLoading(false);
    }
  }

  const updateDateRange = (startDate: Dayjs, endDate: Dayjs, newDateRange: string) => {
    setWeekFilterProps({
      ...weekFilterProps,
      startDate: startDate,
      endDate: endDate,
    });
    setYear(endDate.year());
    setFilteredWeek(getWeekNumber(endDate));
    setDateRange(newDateRange);
  }
  return (
    <>
      <FancyPaper
        pagetitle="Detalles de Avances"
        prevpage="Avances"
        linkprevpage="/dashboard/advances"
        showHome={authInfo?.userRole?.roleName !== "Encargado"}
      >
        <Grid
          container
          spacing={6}
          sx={{
            background: theme.palette.secondary.light,
            borderRadius: 2,
            padding: 4,
            marginBottom: 6,
            marginTop: 4,
            marginLeft: 4,
          }}
        >
          {/* Selector de Semana */}
          <Grid item xs={12} md={3} sx={{ marginBottom: 4 }}>
            <WeekFilter weekFilterDateRangeProps={weekFilterProps} onRangeChange={updateDateRange}/>
          </Grid>

          {/* Selector de Proyecto y Avance */}
          <Grid
            item
            xs={12}
            md={3}
            sx={{ marginTop: 2, marginLeft: 40, marginRight: 25 }}
          >
            <Autocomplete
              disablePortal
              options={projects.map((project) => ({
                id: project.id,
                name: project.name,
                code: project.code,
                manager: project.manager,
                attendand: project.attendand,
              }))}
              sx={{ width: "100%" }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              getOptionLabel={(option) => `${option.code} - ${option.name}`}
              value={selectedProject}
              onChange={(event, newValue) => newValue && handleChange(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Seleccione un Proyecto"
                  InputLabelProps={{ shrink: true }}
                />
              )}
              disabled={advanceData?.id || id}
            />
            <Grid sx={{ marginTop: 8 }}>
              <Typography variant="body2" fontWeight="bold">
                Avance:
              </Typography>
              <Typography>{selectedProject?.name}</Typography>
            </Grid>
          </Grid>

          {/* Encargado y Subencargado */}
          <Grid item xs={12} md={3} sx={{ marginBottom: 4 }}>
            <Typography variant="body2" fontWeight="bold">
              Encargado:
            </Typography>
            <Typography>{selectedProject?.manager}</Typography>
            <Grid sx={{ marginTop: 8 }}>
              <Typography variant="body2" fontWeight="bold">
                Subencargado:
              </Typography>
              <Typography>{selectedProject?.attendand}</Typography>
            </Grid>
          </Grid>
        </Grid>

        {/* Botón para crear avance */}
        {filteredWeek && selectedProject && !advanceCreated && !id && (
          <Grid container justifyContent="right" sx={{ mt: 4 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleCreateAdvance}
            >
              Crear Avance
            </Button>
          </Grid>
        )}

        {(advanceCreated || id) && (
          <Grid container justifyContent="space-between" alignItems="center" sx={{ mt: 4 }}>
            {/* Información de última modificación */}
            <Grid item>
              {details?.header?.lastModifiedBy && (
                <Typography variant="body2" color="textSecondary">
                  Última modificación por: {details.header.lastModifiedBy}
                </Typography>
              )}
            </Grid>
            
            {/* Botones */}
            <Grid item>
              <Grid container spacing={2}>
                {details?.header?.editable === true &&
                  <Grid item>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<EventRepeat />}
                        onClick={showRescheduleProjectConfirmationMessage}
                    >
                      Mover de semana
                    </Button>
                  </Grid>
                }
                <Grid item>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenHoursModal(dateRange)}
                    disabled={(details && !details?.header?.editable) || (advanceData && !advanceData?.editable)} 
                  >
                    Agregar Horas
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        )}

        <Divider sx={{ my: 3 }} />

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell align="center">ID</TableCell>
                  <TableCell align="center">Nombre</TableCell>
                  <TableCell align="center">Cargo</TableCell>
                  <TableCell align="center">Monto por Hora</TableCell>
                  <TableCell align="center">J</TableCell>
                  <TableCell align="center">V</TableCell>
                  <TableCell align="center">S</TableCell>
                  <TableCell align="center">D</TableCell>
                  <TableCell align="center">L</TableCell>
                  <TableCell align="center">K</TableCell>
                  <TableCell align="center">M</TableCell>
                  <TableCell align="center">Total Horas</TableCell>
                  <ResourceAccess isCode={true} pathOrCode={"ADVANCES:READCOLUMNS"}>
                    <TableCell align="center">Total Horas Extra</TableCell>

                    <FancyTooltip
                      title="Refleja el salario bruto del colaborador (incluye horas extra y dobles)"
                      placement="top"
                    >
                      <TableCell align="center">Subtotal</TableCell>
                    </FancyTooltip>

                    <TableCell align="center">Deducción CCSS</TableCell>
                    <TableCell align="center">Créditos / Débitos</TableCell>
                    <TableCell align="center">Total a Pagar</TableCell>
                    <TableCell align="center">Pasajes</TableCell>
                  </ResourceAccess>
                  <TableCell align="center">Acciones</TableCell>
                </TableRow>
              </TableHead>
              {details?.details &&
              <TableBody>
                {details.details.map((detail: any) => (
                  <TableRow key={detail.id}>
                    <TableCell align="center">
                      <span>
                        {details.overtimeWorkedHoursWarning?.[detail.id] && (
                            <Tooltip
                              title={
                                <div>
                                  {details.overtimeWorkedHoursWarning[detail.id].map(
                                    (warning: any, index: any) => (
                                      <div key={index}>{warning}</div> // Muestra cada advertencia en una línea separada
                                    )
                                  )}
                                </div>
                              }
                              placement="top"
                              sx={{
                                marginLeft: "-20px",
                                marginRight: "4px",
                                transform: "translateY(4px)",
                              }}
                            >
                              <WarningIcon color="warning" />
                            </Tooltip>
                          )}
                        {detail.id}
                      </span>
                    </TableCell>
                    <TableCell align="center">{detail.name}</TableCell>
                    <TableCell align="center">{detail.position}</TableCell>
                    <TableCell align="center">
                      {formatNumber(detail.salaryPerHour)}
                    </TableCell>

                    {/* Días de la semana */}
                    {WEEK_DAYS.map((day) => (
                      <WorkedHoursDayCell
                        key={day}
                        hours={detail[`${day}ToShowWorkedHours`]}
                        isHoliday={!!details?.holidays?.includes(day)}
                        isVacation={!!details?.vacations?.[detail.id]?.includes(day)}
                      />
                    ))}

                    <TableCell align="center">{formatHours(detail.workedHours)}</TableCell>
                    <ResourceAccess isCode={true} pathOrCode={"ADVANCES:READCOLUMNS"}>
                      <TableCell align="center">
                        {formatHours(detail.overtimeWorkedHours)}
                      </TableCell>
                      <TableCell align="center">
                        {formatNumber(detail.grossSalary)}
                      </TableCell>
                      <TableCell align="center">
                        {formatNumber(detail.ccssAmount)}
                      </TableCell>
                      <TableCell align="center">
                        {formatNumber(detail.creditDebitAmount)}
                      </TableCell>
                      <TableCell align="center">
                        {formatNumber(detail.netSalary)}
                      </TableCell>
                      <TableCell align="center">
                        {formatNumber(detail.transportationPaymentAmount)}
                      </TableCell>
                    </ResourceAccess>
                    <TableCell align="center">
                      <FancyTooltip title="Editar" placement="top">
                        <IconButton
                          aria-label="edit"
                          color="primary"
                          data-id={detail.id}
                          data-position-id={detail.positionId}
                          data-name="edit"
                          onClick={handleOpenModal}
                          disabled={!details?.header?.editable} // Deshabilitar si editable es false
                        >
                          <PenIcon />
                        </IconButton>
                      </FancyTooltip>
                      <FancyTooltip title="Eliminar" placement="top">
                        <IconButton
                          aria-label="trash"
                          color="error"
                          data-id={detail.id}
                          data-position-id={detail.positionId}
                          data-name="delete"
                          onClick={handleOpenModal}
                          disabled={!details?.header?.editable} // Deshabilitar si editable es false
                        >
                          <TrashIcon />
                        </IconButton>
                      </FancyTooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              }
            </Table>
          </TableContainer>
      </FancyPaper>
      {modalData?.modalType === "addHours" && (
        <Dialog
          open={modalData.modalOpen}
          onClose={handleCloseHoursModal}
          maxWidth="lg"
          fullWidth
        >
          <AddOrUpdateCoworkerModal
            data={{
              modalObject: modalData,
              advanceData: advanceData,
              advanceId: id,
              year: year,
              week: filteredWeek,
              user: authInfo?.username, // Agregar username
            }}
            cancelModal={handleCloseHoursModal}
            coworkersData={projectCoworkersData}
            getData={fetchProjects}
          />
        </Dialog>
      )}
      {modalData?.modalType === "edit" && (
        <Dialog
          open={modalData.modalOpen}
          onClose={handleCloseHoursModal}
          maxWidth="lg"
          fullWidth
        >
          <AddOrUpdateCoworkerModal
            data={{
              modalObject: { modalObject: dateRange }, // Usar el dateRange disponible
              advanceData: advanceData,
              advanceId: id,
              year: year,
              week: filteredWeek,
              editData: modalData?.modalObject, // Datos para editar
              isEdit: true, // Flag para indicar que es edición
              user: authInfo?.username, // Agregar username
            }}
            cancelModal={handleCloseHoursModal}
            coworkersData={coworkersData}
            getData={fetchProjects}
          />
        </Dialog>
      )}
      {modalData?.modalType === "delete" && (
        <Dialog
          open={modalData.modalOpen}
          onClose={handleCloseHoursModal}
          fullWidth
        >
          <DeleteModal
            //@ts-ignore
            textChildren={modalData?.modalObject?.code}
            actionButton={
              <>
                <FancyDialogActions
                  handleAccept={handleSubmit(onSubmit)}
                  cancelModal={handleCloseHoursModal}
                  textAcceptButton={"Eliminar"}
                />
              </>
            }
          />
        </Dialog>
      )}
      <Dialog open={showModal} onClose={hideModal}>
        <DialogContent>
          <Typography>{dialogMessage}</Typography>
        </DialogContent>
        <FancyDialogActions
            handleAccept={action}
            cancelModal={hideModal}
            textAcceptButton="Aceptar"
            textCancelButton="Cancelar"
        />
      </Dialog>
    </>
  );
};

export default DetailsAdvancesAdd;
